import { cn } from '@shared/components/shadcn';
import { VipFilled } from '@shared/icons';
import type { ReactNode } from 'react';
import { Callout } from '../../../../Callout/Callout';
import { OfferValidityBadge } from '../../../../Offer/components/OfferValidityBadge/OfferValidityBadge';

type CRMContentProps = {
  title?: string;
  subheading?: string;
  children?: ReactNode;
  validUntilDate?: string;
  callout?: string;
  sectionClassName?: string;
};

/**
 * CRMContent component for landing pages
 *
 * Displays the main content section with headline, subheading, optional validity badge, optional VIP callout, and optional children content.
 *
 * @param title - Main headline text
 * @param subheading - Optional subheading text
 * @param validUntilDate - Optional validity date string for displaying OfferValidityBadge
 * @param callout - Optional callout text to display alongside the validity badge
 * @param children - Optional React node to render as additional content
 * @param sectionClassName - Optional class names for section
 */
function CRMContent({ title, subheading, children, validUntilDate, callout, sectionClassName }: CRMContentProps): React.JSX.Element | null {
  if (!title?.trim() && !subheading?.trim() && !children) {
    return null;
  }

  return (
    <section className={cn('flex flex-col gap-4', sectionClassName)} data-testid="crm-content">
      {(validUntilDate || callout) && (
        <div className="flex flex-wrap items-center gap-2">
          {validUntilDate && (
            <div className="shrink-0 whitespace-nowrap">
              <OfferValidityBadge validUntil={validUntilDate} data-testid="network-promo-validity-badge" />
            </div>
          )}
          {callout && (
            <div className="shrink-0 whitespace-nowrap">
              <Callout variant="vip" data-testid="crm-content-vip-callout">
                <VipFilled size="extra-small" aria-hidden="true" />
                {callout}
              </Callout>
            </div>
          )}
        </div>
      )}
      {title && (
        <h1 className="heading-4 text-foreground" data-testid="crm-content-title">
          {title}
        </h1>
      )}

      {subheading && (
        <p className="paragraph text-foreground" data-testid="crm-content-subheading">
          {subheading}
        </p>
      )}

      {children}
    </section>
  );
}

export { CRMContent, type CRMContentProps };
